import React, { Component } from "react"
import { connect } from "react-redux"
import { Field, formValueSelector, reduxForm } from "redux-form"
import { Link } from "react-router-dom"
import styled from "styled-components"
import {
  AVATAR_CATEGORIES,
  AVATARS_BY_CATEGORY,
  avatarUrl
} from "../../common/util/avatars"

const ASSETS = `${process.env.PUBLIC_URL}/assets`
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const DEFAULT_SIGNUP_AVATAR = AVATARS_BY_CATEGORY.real[0]
const INITIAL_VALUES = { avatar: DEFAULT_SIGNUP_AVATAR }
const REDUCED_MOTION_PREFERRED =
  typeof window !== "undefined" &&
  window.matchMedia &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches

const RegistrationPanel = styled.section`
  position: relative;
  box-sizing: border-box;
  width: 100%;
  background: rgba(2, 0, 26, 0.93);
  border: 1px solid rgba(252, 38, 250, 0.55);
  box-shadow: 0 0 24px rgba(252, 38, 250, 0.2);
`

const PanelCorner = styled.span`
  position: absolute;
  z-index: 2;
  width: 14px;
  height: 14px;
  pointer-events: none;

  &.top-left {
    top: -1px;
    left: -1px;
    border-top: 2px solid rgba(252, 38, 250, 0.9);
    border-left: 2px solid rgba(252, 38, 250, 0.9);
  }

  &.top-right {
    top: -1px;
    right: -1px;
    border-top: 2px solid rgba(252, 38, 250, 0.9);
    border-right: 2px solid rgba(252, 38, 250, 0.9);
  }

  &.bottom-left {
    bottom: -1px;
    left: -1px;
    border-bottom: 2px solid rgba(252, 38, 250, 0.9);
    border-left: 2px solid rgba(252, 38, 250, 0.9);
  }

  &.bottom-right {
    right: -1px;
    bottom: -1px;
    border-right: 2px solid rgba(252, 38, 250, 0.9);
    border-bottom: 2px solid rgba(252, 38, 250, 0.9);
  }
`

const PanelHeader = styled.header`
  display: flex;
  align-items: baseline;
  gap: 16px;
  padding: 18px 26px;
  border-bottom: 1px solid rgba(252, 38, 250, 0.25);

  @media (max-width: 760px) {
    align-items: flex-start;
    flex-direction: column;
    gap: 3px;
    padding: 15px 16px;
  }
`

const PanelTitle = styled.h1`
  margin: 0;
  color: #fc26fa;
  font-family: "Rajdhani", sans-serif;
  font-size: 20px;
  font-weight: 700;
  letter-spacing: 3px;
  text-shadow: 0 0 8px rgba(252, 38, 250, 0.4);

  @media (max-width: 420px) {
    font-size: 18px;
    letter-spacing: 2px;
  }
`

const Step = styled.div`
  margin-left: auto;
  color: #6d7da6;
  font-family: "VT323", monospace;
  font-size: 14px;
  letter-spacing: 1px;

  @media (max-width: 760px) {
    margin-left: 0;
  }
`

const PanelBody = styled.div`
  display: grid;
  grid-template-columns: 300px minmax(0, 1fr);
  grid-template-areas:
    "hero form"
    "preview form";
  column-gap: 28px;
  row-gap: 12px;
  padding: 24px 26px 20px;

  @media (max-width: 900px) {
    grid-template-columns: minmax(0, 1fr);
    grid-template-areas:
      "hero"
      "form";
    gap: 18px;
    padding: 16px 14px 18px;
  }
`

const PreviewColumn = styled.aside`
  grid-area: preview;
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 0;

  @media (max-width: 900px) {
    display: none;
  }
`

const HeroFrame = styled.div`
  position: relative;
  grid-area: hero;
  display: block;
  box-sizing: border-box;
  width: 100%;
  aspect-ratio: 418 / 246;
  overflow: hidden;
  border: 1px solid rgba(190, 38, 252, 0.5);

  @media (max-width: 900px) {
    height: 108px;
    aspect-ratio: auto;
  }
`

const HeroPicture = styled.picture`
  display: block;
  width: 100%;
  height: 100%;
`

const HeroImage = styled.img`
  display: block;
  box-sizing: border-box;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
`

const HeroToggle = styled.button`
  position: absolute;
  right: 8px;
  bottom: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 76px;
  min-height: 44px;
  padding: 4px 10px;
  background: rgba(2, 0, 26, 0.86);
  border: 1px solid rgba(0, 231, 255, 0.75);
  color: #00e7ff;
  font-family: "VT323", monospace;
  font-size: 14px;
  letter-spacing: 1px;
  cursor: pointer;

  &:focus {
    outline: 2px solid #fc26fa;
    outline-offset: 2px;
  }
`

const PreviewLabel = styled.div`
  color: #6d7da6;
  font-family: "VT323", monospace;
  font-size: 13px;
  letter-spacing: 1px;
`

const RosterCard = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  flex-direction: column;
  gap: 6px;
  padding: 16px;
  overflow: hidden;
  background: rgba(0, 30, 5, 0.22);
  border: 1px solid rgba(23, 255, 14, 0.45);
`

const RosterCorner = styled.span`
  position: absolute;
  width: 10px;
  height: 10px;
  pointer-events: none;

  &.top-left {
    top: -1px;
    left: -1px;
    border-top: 2px solid rgba(23, 255, 14, 0.9);
    border-left: 2px solid rgba(23, 255, 14, 0.9);
  }

  &.top-right {
    top: -1px;
    right: -1px;
    border-top: 2px solid rgba(23, 255, 14, 0.9);
    border-right: 2px solid rgba(23, 255, 14, 0.9);
  }

  &.bottom-left {
    bottom: -1px;
    left: -1px;
    border-bottom: 2px solid rgba(23, 255, 14, 0.9);
    border-left: 2px solid rgba(23, 255, 14, 0.9);
  }

  &.bottom-right {
    right: -1px;
    bottom: -1px;
    border-right: 2px solid rgba(23, 255, 14, 0.9);
    border-bottom: 2px solid rgba(23, 255, 14, 0.9);
  }
`

const PreviewAvatar = styled.img`
  display: block;
  width: 84px;
  height: 84px;
  overflow: hidden;
  object-fit: cover;
  background: #02121a;
  border: 1px solid rgba(0, 231, 255, 0.55);
  box-shadow: 0 0 6px rgba(0, 231, 255, 0.5);
`

const PreviewHandle = styled.div`
  max-width: 100%;
  overflow: hidden;
  color: #00e7ff;
  font-family: "VT323", monospace;
  font-size: 16px;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const MessageCount = styled.div`
  margin-top: -2px;
  color: #ffb800;
  font-family: "VT323", monospace;
  font-size: 13px;
`

const RegisteredToday = styled.div`
  margin-top: -2px;
  color: #d1ffdb;
  font-family: "VT323", monospace;
  font-size: 12px;
`

const FormColumn = styled.div`
  grid-area: form;
  display: flex;
  flex-direction: column;
  min-width: 0;
`

const MobileRosterPreview = styled.div`
  display: none;

  @media (max-width: 900px) {
    display: grid;
    grid-template-columns: 52px minmax(0, 1fr);
    align-items: center;
    gap: 10px;
    margin-top: 10px;
    padding: 9px;
    background: rgba(0, 30, 5, 0.22);
    border: 1px solid rgba(23, 255, 14, 0.45);
  }
`

const MobileRosterAvatar = styled.img`
  display: block;
  box-sizing: border-box;
  width: 52px;
  height: 52px;
  object-fit: cover;
  border: 1px solid rgba(0, 231, 255, 0.7);
  box-shadow: 0 0 6px rgba(0, 231, 255, 0.45);
`

const MobileRosterCopy = styled.div`
  min-width: 0;
`

const MobileRosterLabel = styled.div`
  color: #17ff0e;
  font-family: "VT323", monospace;
  font-size: 12px;
  letter-spacing: 1px;
`

const MobileRosterHandle = styled.div`
  overflow: hidden;
  color: #00e7ff;
  font-family: "VT323", monospace;
  font-size: 17px;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const FieldLabel = styled.label`
  margin-bottom: 6px;
  color: #00e7ff;
  font-family: "VT323", monospace;
  font-size: 14px;
  letter-spacing: 2px;

  &.spaced {
    margin-top: 12px;
  }
`

const ThemedInput = styled.input`
  box-sizing: border-box;
  width: 100%;
  min-height: 44px;
  padding: 10px 12px;
  outline: none;
  background: #02121a;
  border: 1px solid rgba(0, 231, 255, 0.4);
  border-radius: 0;
  color: #00e7ff;
  font-family: "VT323", monospace;
  font-size: 18px;
  letter-spacing: 1px;

  &:focus {
    border-color: #00e7ff;
    box-shadow: 0 0 8px rgba(0, 231, 255, 0.4);
  }

  &::placeholder {
    color: rgba(0, 231, 255, 0.35);
  }
`

const FieldStatus = styled.div`
  min-height: 16px;
  margin-top: 5px;
  font-family: "VT323", monospace;
  font-size: 13px;
  letter-spacing: 1px;
`

const AvatarPicker = styled.div`
  margin-top: 12px;
  min-width: 0;
`

const AvatarPickerHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 8px;
`

const AvatarPickerLabel = styled.div`
  margin-right: auto;
  color: #00e7ff;
  font-family: "VT323", monospace;
  font-size: 14px;
  letter-spacing: 2px;
`

const AvatarTabs = styled.div`
  display: flex;
  gap: 6px;
`

const AvatarTab = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 72px;
  min-height: 44px;
  padding: 4px 12px;
  background: ${p =>
    p.active ? "rgba(0, 231, 255, 0.12)" : "transparent"};
  border: 1px solid
    ${p => (p.active ? "#00e7ff" : "rgba(111, 191, 255, 0.35)")};
  color: ${p => (p.active ? "#00e7ff" : "#6fbfff")};
  font-family: "VT323", monospace;
  font-size: 14px;
  letter-spacing: 1px;
  cursor: pointer;

  &:focus {
    outline: 2px solid #fc26fa;
    outline-offset: 2px;
  }
`

const AvatarRail = styled.div`
  display: flex;
  gap: 8px;
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
  padding: 2px 2px 9px;
  overflow-x: auto;
  overscroll-behavior-x: contain;
  scroll-snap-type: x proximity;
  scrollbar-color: #00e7ff rgba(109, 125, 166, 0.18);
  scrollbar-width: thin;
`

const AvatarRailFrame = styled.div`
  position: relative;
  min-width: 0;

  &::after {
    position: absolute;
    top: 2px;
    right: 0;
    bottom: 9px;
    width: 30px;
    pointer-events: none;
    background: linear-gradient(90deg, transparent, rgba(2, 0, 26, 0.94));
    content: "";
    opacity: ${p => (p.atEnd ? 0 : 1)};
    transition: opacity 140ms ease;
  }
`

const AvatarSwipeHint = styled.div`
  display: none;

  @media (max-width: 900px) {
    display: ${p => (p.atEnd ? "none" : "block")};
    margin-top: -4px;
    color: #6fbfff;
    font-family: "VT323", monospace;
    font-size: 12px;
    letter-spacing: 1px;
    text-align: right;
  }
`

const AvatarChoice = styled.button`
  appearance: none;
  flex: 0 0 56px;
  width: 56px;
  height: 56px;
  padding: 0;
  overflow: hidden;
  scroll-snap-align: start;
  background: #02121a;
  border: 2px solid
    ${p => (p.selected ? "#00e7ff" : "rgba(111, 191, 255, 0.25)")};
  box-shadow: ${p =>
    p.selected
      ? "0 0 10px rgba(0, 231, 255, 0.8)"
      : "none"};
  cursor: pointer;

  &:focus {
    outline: 2px solid #fc26fa;
    outline-offset: 2px;
  }

  img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  @media (max-width: 420px) {
    flex-basis: 52px;
    width: 52px;
    height: 52px;
  }
`

const StrengthRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 8px;
`

const StrengthSegments = styled.div`
  display: flex;
  flex: 1;
  gap: 4px;
`

const StrengthSegment = styled.span`
  flex: 1;
  height: 6px;
`

const StrengthLabel = styled.span`
  flex: none;
  font-family: "VT323", monospace;
  font-size: 13px;
  letter-spacing: 1px;
`

const Requirements = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  margin-top: 8px;
`

const Requirement = styled.span`
  font-family: "VT323", monospace;
  font-size: 13px;
`

const ErrorBanner = styled.div`
  margin-top: 12px;
  padding: 8px 10px;
  background: rgba(255, 0, 28, 0.08);
  border: 1px solid rgba(255, 0, 28, 0.6);
  color: #ff6073;
  font-family: "VT323", monospace;
  font-size: 14px;
  letter-spacing: 1px;
`

const SubmitButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 46px;
  margin-top: 18px;
  background: rgba(23, 255, 14, 0.1);
  border: 1px solid rgba(23, 255, 14, 0.8);
  color: #17ff0e;
  font-family: "Rajdhani", sans-serif;
  font-size: 16px;
  font-weight: 700;
  letter-spacing: 3px;
  opacity: ${p => (p.disabled ? 0.45 : 1)};
  cursor: ${p => (p.disabled ? "not-allowed" : "pointer")};

  &:not(:disabled):hover {
    background: rgba(23, 255, 14, 0.18);
    box-shadow: 0 0 14px rgba(23, 255, 14, 0.35);
  }
`

const BottomRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 14px;

  @media (max-width: 420px) {
    align-items: flex-start;
    flex-direction: column;
    gap: 5px;
  }
`

const LoginLink = styled(Link)`
  color: #00e7ff;
  font-family: "VT323", monospace;
  font-size: 14px;
  text-decoration: none;

  &:hover {
    color: #fc26fa;
  }

  @media (max-width: 760px) {
    display: inline-flex;
    align-items: center;
    min-height: 44px;
  }
`

const SystemVersion = styled.span`
  margin-left: auto;
  color: #3a4670;
  font-family: "VT323", monospace;
  font-size: 12px;

  @media (max-width: 420px) {
    margin-left: 0;
  }
`

const themedField = ({
  input,
  type,
  placeholder,
  id,
  autoComplete,
  maxLength,
  ariaLabel,
  describedBy,
  invalid
}) => (
  <ThemedInput
    {...input}
    id={id}
    type={type}
    placeholder={placeholder}
    autoComplete={autoComplete}
    maxLength={maxLength}
    aria-label={ariaLabel}
    aria-describedby={describedBy}
    aria-invalid={invalid}
  />
)

const hiddenField = ({ input }) => <input {...input} type="hidden" />

const validate = values => {
  const errors = {}
  const handle = (values.name || "").trim()
  const email = (values.email || "").trim()
  const password = values.password || ""

  if (handle.length < 2) errors.name = "Choose a handle"
  if (!EMAIL_PATTERN.test(email)) errors.email = "Enter a valid email"
  if (password.length < 8 || !/[0-9]/.test(password) || !/[^a-zA-Z0-9]/.test(password)) {
    errors.password = "Access code requirements are incomplete"
  }

  return errors
}

class Signup extends Component {
  state = {
    avatarCategory: AVATAR_CATEGORIES[0].id,
    animationEnabled: !REDUCED_MOTION_PREFERRED,
    avatarRailAtEnd: false
  }

  selectAvatarCategory = avatarCategory => {
    this.setState({ avatarCategory, avatarRailAtEnd: false })
  }

  handleAvatarRailScroll = event => {
    const { clientWidth, scrollLeft, scrollWidth } = event.currentTarget
    const avatarRailAtEnd = scrollLeft + clientWidth >= scrollWidth - 2
    if (avatarRailAtEnd !== this.state.avatarRailAtEnd) {
      this.setState({ avatarRailAtEnd })
    }
  }

  handleAvatarTabKeyDown = (event, currentIndex) => {
    const lastIndex = AVATAR_CATEGORIES.length - 1
    let nextIndex = currentIndex

    if (event.key === "ArrowRight" || event.key === "ArrowDown") {
      nextIndex = currentIndex === lastIndex ? 0 : currentIndex + 1
    } else if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
      nextIndex = currentIndex === 0 ? lastIndex : currentIndex - 1
    } else if (event.key === "Home") {
      nextIndex = 0
    } else if (event.key === "End") {
      nextIndex = lastIndex
    } else {
      return
    }

    event.preventDefault()
    this.selectAvatarCategory(AVATAR_CATEGORIES[nextIndex].id)
    event.currentTarget.parentElement.children[nextIndex].focus()
  }

  selectAvatar = avatar => {
    this.props.change("avatar", avatar)
    if (this.props.onFieldChange) {
      this.props.onFieldChange()
    }
  }

  handleAvatarChoiceKeyDown = (event, currentIndex) => {
    const avatars = AVATARS_BY_CATEGORY[this.state.avatarCategory]
    const lastIndex = avatars.length - 1
    let nextIndex = currentIndex

    if (event.key === "ArrowRight" || event.key === "ArrowDown") {
      nextIndex = currentIndex === lastIndex ? 0 : currentIndex + 1
    } else if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
      nextIndex = currentIndex === 0 ? lastIndex : currentIndex - 1
    } else if (event.key === "Home") {
      nextIndex = 0
    } else if (event.key === "End") {
      nextIndex = lastIndex
    } else {
      return
    }

    event.preventDefault()
    this.selectAvatar(avatars[nextIndex])
    event.currentTarget.parentElement.children[nextIndex].focus()
  }

  toggleAnimation = () => {
    this.setState(state => ({ animationEnabled: !state.animationEnabled }))
  }

  render() {
    const {
      handleSubmit,
      formValues = {},
      signupError,
      onFieldChange,
      submitting
    } = this.props
    const handle = (formValues.name || "").trim()
    const email = (formValues.email || "").trim()
    const password = formValues.password || ""
    const selectedAvatar = formValues.avatar || DEFAULT_SIGNUP_AVATAR
    const avatarCategory = this.state.avatarCategory
    const animationEnabled = this.state.animationEnabled
    const avatarRailAtEnd = this.state.avatarRailAtEnd
    const categoryHasSelection = AVATARS_BY_CATEGORY[avatarCategory].includes(
      selectedAvatar
    )
    const handleReady = handle.length >= 2
    const emailReady = EMAIL_PATTERN.test(email)
    const hasLength = password.length >= 8
    const hasNumber = /[0-9]/.test(password)
    const hasSymbol = /[^a-zA-Z0-9]/.test(password)
    let strength = 0
    if (hasLength) strength += 1
    if (hasNumber) strength += 1
    if (hasSymbol) strength += 1
    if (password.length >= 12) strength += 1

    const strengthMeta = [
      { label: "NO SIGNAL", color: "#3a4670" },
      { label: "WEAK", color: "#fc26fa" },
      { label: "OK", color: "#ffb800" },
      { label: "STRONG", color: "#17ff0e" },
      { label: "ENCRYPTED", color: "#00e7ff" }
    ][strength]
    const segmentColor = strengthMeta.color
    const canSubmit =
      handleReady && emailReady && hasLength && hasNumber && hasSymbol
    const disabled = !canSubmit || submitting
    const previewHandle = handle || "UNNAMED"

    return (
      <RegistrationPanel aria-labelledby="signup-title">
        <PanelCorner className="top-left" />
        <PanelCorner className="top-right" />
        <PanelCorner className="bottom-left" />
        <PanelCorner className="bottom-right" />

        <PanelHeader>
          <PanelTitle id="signup-title">NEW USER REGISTRATION</PanelTitle>
          <Step>STEP 01 // IDENTITY</Step>
        </PanelHeader>

        <PanelBody>
          <HeroFrame>
            <HeroPicture>
              <HeroImage
                src={`${ASSETS}/${
                  animationEnabled ? "cyberpunk.gif" : "hero-pixel.png"
                }`}
                alt="Cyberpunk grid skyline"
              />
            </HeroPicture>
            <HeroToggle
              type="button"
              aria-label={
                animationEnabled
                  ? "Pause animated signup scene"
                  : "Play animated signup scene"
              }
              onClick={this.toggleAnimation}
            >
              {animationEnabled ? "Ⅱ PAUSE" : "▶ PLAY"}
            </HeroToggle>
          </HeroFrame>

          <PreviewColumn>
            <PreviewLabel>
              ROSTER PREVIEW // HOW THE GRID SEES YOU
            </PreviewLabel>
            <RosterCard>
              <RosterCorner className="top-left" />
              <RosterCorner className="top-right" />
              <RosterCorner className="bottom-left" />
              <RosterCorner className="bottom-right" />
              <PreviewAvatar
                src={avatarUrl(selectedAvatar)}
                alt="Selected roster avatar"
              />
              <PreviewHandle title={previewHandle}>
                {previewHandle}
              </PreviewHandle>
              <MessageCount>MSGS: 0</MessageCount>
              <RegisteredToday>registered: today</RegisteredToday>
            </RosterCard>
          </PreviewColumn>

          <FormColumn as="form" onSubmit={handleSubmit} noValidate>
            <Field name="avatar" component={hiddenField} />
            <FieldLabel htmlFor="signup-handle">HANDLE</FieldLabel>
            <Field
              name="name"
              id="signup-handle"
              type="text"
              placeholder="e.g. NightRunner"
              autoComplete="nickname"
              maxLength={30}
              ariaLabel="Handle"
              describedBy="signup-handle-status"
              invalid={!handleReady}
              onChange={onFieldChange}
              component={themedField}
            />
            <FieldStatus
              id="signup-handle-status"
              role="status"
              style={{ color: handleReady ? "#17ff0e" : "#6d7da6" }}
            >
              {handleReady
                ? "✓ HANDLE READY FOR ROSTER"
                : "> choose your grid handle"}
            </FieldStatus>

            <AvatarPicker>
              <AvatarPickerHeader>
                <AvatarPickerLabel id="signup-avatar-label">
                  AVATAR // VISUAL ID
                </AvatarPickerLabel>
                <AvatarTabs role="tablist" aria-label="Avatar style">
                  {AVATAR_CATEGORIES.map((category, categoryIndex) => {
                    const active = category.id === avatarCategory
                    return (
                      <AvatarTab
                        key={category.id}
                        id={`signup-avatar-tab-${category.id}`}
                        type="button"
                        role="tab"
                        active={active}
                        tabIndex={active ? 0 : -1}
                        aria-selected={active}
                        aria-controls="signup-avatar-choices"
                        onKeyDown={event =>
                          this.handleAvatarTabKeyDown(event, categoryIndex)
                        }
                        onClick={() =>
                          this.selectAvatarCategory(category.id)
                        }
                      >
                        {category.label}
                      </AvatarTab>
                    )
                  })}
                </AvatarTabs>
              </AvatarPickerHeader>
              <AvatarRailFrame atEnd={avatarRailAtEnd}>
                <AvatarRail
                  key={avatarCategory}
                  id="signup-avatar-choices"
                  role="tabpanel"
                  aria-labelledby={`signup-avatar-tab-${avatarCategory}`}
                  onScroll={this.handleAvatarRailScroll}
                >
                  {AVATARS_BY_CATEGORY[avatarCategory].map(
                    (avatar, avatarIndex) => {
                      const isSelected = avatar === selectedAvatar
                      const isTabStop =
                        isSelected || (!categoryHasSelection && avatarIndex === 0)
                      return (
                        <AvatarChoice
                          key={avatar}
                          type="button"
                          selected={isSelected}
                          tabIndex={isTabStop ? 0 : -1}
                          aria-label={`Use ${avatar} avatar`}
                          aria-pressed={isSelected}
                          onKeyDown={event =>
                            this.handleAvatarChoiceKeyDown(event, avatarIndex)
                          }
                          onClick={() => this.selectAvatar(avatar)}
                        >
                          <img src={avatarUrl(avatar)} alt="" />
                        </AvatarChoice>
                      )
                    }
                  )}
                </AvatarRail>
              </AvatarRailFrame>
              <AvatarSwipeHint atEnd={avatarRailAtEnd} aria-hidden="true">
                SWIPE FOR MORE ▸
              </AvatarSwipeHint>
            </AvatarPicker>

            <MobileRosterPreview>
              <MobileRosterAvatar
                src={avatarUrl(selectedAvatar)}
                alt="Selected avatar preview"
              />
              <MobileRosterCopy>
                <MobileRosterLabel>LIVE ROSTER PREVIEW</MobileRosterLabel>
                <MobileRosterHandle title={previewHandle}>
                  {previewHandle}
                </MobileRosterHandle>
              </MobileRosterCopy>
            </MobileRosterPreview>

            <FieldLabel className="spaced" htmlFor="signup-email">
              EMAIL
            </FieldLabel>
            <Field
              name="email"
              id="signup-email"
              type="email"
              placeholder="you@uplink.net"
              autoComplete="email"
              maxLength={200}
              ariaLabel="Email"
              describedBy="signup-email-status"
              invalid={!emailReady}
              onChange={onFieldChange}
              component={themedField}
            />
            <FieldStatus
              id="signup-email-status"
              role="status"
              style={{
                color: emailReady
                  ? "#17ff0e"
                  : email
                    ? "#fc26fa"
                    : "#6d7da6"
              }}
            >
              {emailReady
                ? "✓ UPLINK ADDRESS LOCKED"
                : email
                  ? "✗ INVALID UPLINK ADDRESS"
                  : "> provide your secure uplink"}
            </FieldStatus>

            <FieldLabel className="spaced" htmlFor="signup-password">
              ACCESS CODE
            </FieldLabel>
            <Field
              name="password"
              id="signup-password"
              type="password"
              placeholder="min 8 characters"
              autoComplete="new-password"
              ariaLabel="Access code"
              describedBy="signup-password-strength signup-password-requirements"
              invalid={!hasLength || !hasNumber || !hasSymbol}
              onChange={onFieldChange}
              component={themedField}
            />

            <StrengthRow
              id="signup-password-strength"
              aria-live="polite"
              aria-label={`Access code strength: ${strengthMeta.label}`}
            >
              <StrengthSegments aria-hidden="true">
                {[0, 1, 2, 3].map(index => (
                  <StrengthSegment
                    key={index}
                    style={{
                      background:
                        index < strength
                          ? segmentColor
                          : "rgba(109, 125, 166, 0.25)"
                    }}
                  />
                ))}
              </StrengthSegments>
              <StrengthLabel style={{ color: strengthMeta.color }}>
                {strengthMeta.label}
              </StrengthLabel>
            </StrengthRow>

            <Requirements id="signup-password-requirements">
              <Requirement
                style={{ color: hasLength ? "#17ff0e" : "#3a4670" }}
              >
                {hasLength ? "✓" : "·"} 8+ CHARACTERS
              </Requirement>
              <Requirement
                style={{ color: hasNumber ? "#17ff0e" : "#3a4670" }}
              >
                {hasNumber ? "✓" : "·"} CONTAINS A NUMBER
              </Requirement>
              <Requirement
                style={{ color: hasSymbol ? "#17ff0e" : "#3a4670" }}
              >
                {hasSymbol ? "✓" : "·"} CONTAINS A SYMBOL
              </Requirement>
            </Requirements>

            {signupError && (
              <ErrorBanner role="alert">
                REGISTRATION FAILED // {signupError}
              </ErrorBanner>
            )}

            <SubmitButton type="submit" disabled={disabled}>
              {submitting ? "ESTABLISHING UPLINK…" : "CREATE ACCOUNT ▸"}
            </SubmitButton>

            <BottomRow>
              <LoginLink to="/">ALREADY REGISTERED? JACK IN ▸</LoginLink>
              <SystemVersion>SYS.REG v2.1</SystemVersion>
            </BottomRow>
          </FormColumn>
        </PanelBody>
      </RegistrationPanel>
    )
  }
}

const FORM_NAME = "signup"
const form = reduxForm({ form: FORM_NAME, validate })(Signup)
const selector = formValueSelector(FORM_NAME)
const mapStateToProps = state => ({
  formValues: selector(state, "name", "email", "password", "avatar"),
  initialValues: INITIAL_VALUES
})

export default connect(mapStateToProps)(form)
